import React, { useEffect, useState } from "react";
import moment from "moment";
import dateFns from "date-fns";

import { CalendarItemContainer } from "./calendar-item-style";
import CalendarHeader from "./components/calendar-header";
import CalendarDaysNames from "./components/calendar-days-names";
import CalendarCells from "./components/calendar-cells";
import CalendarYears from "./components/calendar-years";
import CalendarMonths from "./components/calendar-months";
import { isValidDate } from "../../../../utils/time";

interface CalendarItemProps {
  date: string;
  maxDate?: string;
  minDate?: string;
  onSelect: (value: string, closeCalendar: boolean) => void;
}

const CalendarItem = ({ date, maxDate, minDate, onSelect }: CalendarItemProps) => {
  const [showYears, setShowYears] = useState(false);
  const [showMonths, setShowMonths] = useState(false);
  const [newDate, setNewDate] = useState<Date>(moment().toDate());

  useEffect(() => {
    let changedDate = moment().toDate();
    if (!date && minDate) {
      changedDate = moment(minDate).toDate();
    }
    if (date && isValidDate(date)) {
      changedDate = moment(date, "DD/MM/YYYY").toDate();
      if (newDate && changedDate.getTime() === newDate.getTime()) return;
    }
    setNewDate(changedDate);
  }, [minDate, date]);

  const isAllowSelect = (clickedDate: Date, monthSearch = false, yearSearch = false): boolean => {
    const day = moment(clickedDate).startOf("day");
    const min = minDate ? moment(minDate).startOf("day") : null;
    const max = maxDate ? moment(maxDate).startOf("day") : null;

    // Validación años en el header
    if (yearSearch) {
      if (min && max) {
        return day.year() >= min.year() && day.year() <= max.year();
      }
      if (min) return day.year() >= min.year();
      if (max) return day.year() <= max.year();
      return true;
    }

    // Validación meses en el header
    if (monthSearch) {
      if (min && day.isBefore(min, "month")) return false;
      if (max && day.isAfter(max, "month")) return false;
      return true;
    }

    // Validación días
    if ((max && day.isAfter(max)) || (min && day.isBefore(min))) {
      return false;
    }
    return true;
  };

  const getNearestValidDate = (date: Date): Date => {
    let selectedDate = new Date(date);
    if (minDate) {
      const min = new Date(minDate);
      if (selectedDate.getTime() < min.getTime()) return min;
    }
    if (maxDate) {
      const max = new Date(maxDate);
      if (selectedDate.getTime() > max.getTime()) return max;
    }
    return selectedDate;
  };

  const onClickDate = (clickedDate: Date, close: boolean, onlyShowDate = false) => {
    const validDate = getNearestValidDate(clickedDate);
    setNewDate(validDate);
    setShowMonths(false);
    setShowYears(false);
    if (!onlyShowDate) {
      onSelect(dateFns.format(validDate, "DD/MM/YYYY"), close);
    }
  };

  const onClickNextMonth = () => {
    const nextMonth = dateFns.addMonths(newDate, 1);
    if (isAllowSelect(nextMonth, true)) {
      const validDate = getNearestValidDate(nextMonth);
      setNewDate(validDate);
    }
  };

  const onClickPrevMonth = () => {
    const prevMonth = dateFns.subMonths(newDate, 1);
    if (isAllowSelect(prevMonth, true)) {
      const validDate = getNearestValidDate(prevMonth);
      setNewDate(validDate);
    }
  };

  const onClickNextYear = () => {
    const nextYear = dateFns.addYears(newDate, 1);
    if (isAllowSelect(nextYear, false, true)) {
      const validDate = getNearestValidDate(nextYear);
      setNewDate(validDate);
    }
  };

  const onClickPrevYear = () => {
    const prevYear = dateFns.subYears(newDate, 1);
    if (isAllowSelect(prevYear, false, true)) {
      const validDate = getNearestValidDate(prevYear);
      setNewDate(validDate);
    }
  };

  return (
    <CalendarItemContainer>
      <div className="calendar">
        <CalendarHeader
          newDate={newDate}
          setShowMonths={setShowMonths}
          setShowYears={setShowYears}
          onClickPrevMonth={onClickPrevMonth}
          onClickNextMonth={onClickNextMonth}
          onClickNextYear={onClickNextYear}
          onClickPrevYear={onClickPrevYear}
        />
        <CalendarDaysNames newDate={newDate} />
        <CalendarCells newDate={newDate} isAllowSelect={isAllowSelect} onClickDate={onClickDate} />
        <CalendarYears
          showYears={showYears}
          minDate={minDate}
          newDate={newDate}
          isAllowSelect={isAllowSelect}
          onClickDate={onClickDate}
          setShowYears={setShowYears}
        />
        <CalendarMonths
          newDate={newDate}
          maxDate={maxDate}
          minDate={minDate}
          isAllowSelect={isAllowSelect}
          onClickDate={onClickDate}
          showMonths={showMonths}
        />
      </div>
    </CalendarItemContainer>
  );
};

export default CalendarItem;
